/**
 * The terrain model a run is computed on: the site's own DEM, or a GeoTIFF
 * loaded here that passes the site's four checks (CRS, extent, cell size,
 * nodata). A site with no DEM renders nothing.
 */

import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import { api } from '../api/client'

interface DemReport {
  ok: boolean
  checks: { name: string; ok: boolean; detail: string }[]
  info: { crs?: string; resolution_m?: number; extent_coverage?: number; nodata_fraction?: number }
}

interface SiteDem {
  site_id: string
  dem: { value: unknown; source?: string | null }
  available: boolean
  check: DemReport | null
}

interface DemOption {
  /** null = the site DEM, which is what a scenario with no dem_path runs on. */
  path: string | null
  file: string
  origin: string
  report: DemReport | null
}

const fileName = (p: string) => p.split(/[\\/]/).pop() ?? p

/** Each check's measured value, formatted from the report's own numbers. */
function checkRows(report: DemReport) {
  const info = report.info
  const value: Record<string, string | null> = {
    crs: info.crs ?? null,
    extent: info.extent_coverage === undefined ? null : `${(info.extent_coverage * 100).toFixed(1)} %`,
    resolution: info.resolution_m === undefined ? null : `${info.resolution_m.toFixed(1)} m`,
    nodata: info.nodata_fraction === undefined ? null : `${(info.nodata_fraction * 100).toFixed(2)} %`,
  }
  const label: Record<string, string> = { crs: 'CRS', extent: 'Coverage', resolution: 'Cell size', nodata: 'Nodata' }
  return report.checks.map((c) => ({ name: label[c.name] ?? c.name, value: value[c.name] ?? null, ok: c.ok }))
}

export function DemSelector({
  siteId,
  value,
  onChange,
}: {
  siteId: string
  value: string | null
  onChange: (path: string | null) => void
}) {
  const site = useQuery<SiteDem>({
    queryKey: ['site-dem', siteId],
    queryFn: () => api.raw(`/data/site-dem?site_id=${encodeURIComponent(siteId)}`),
  })
  const [loaded, setLoaded] = useState<DemOption[]>([])

  useEffect(() => {
    setLoaded([])
    onChange(null)
    // A loaded DEM belongs to the site it was checked against.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId])

  const load = useMutation({
    mutationFn: async (file: File): Promise<DemOption> => {
      const report = await api.validateDem<DemReport>(file, siteId)
      if (!report.ok) {
        throw new Error(
          report.checks
            .filter((c) => !c.ok)
            .map((c) => `${c.name}: ${c.detail.replace(/\s*\(assumed\)/g, '')}`)
            .join('; '),
        )
      }
      const stored = await api.uploadDemForSite(file, siteId)
      return { path: stored.path, file: file.name, origin: 'Loaded GeoTIFF', report }
    },
    onSuccess: (option) => {
      setLoaded((list) => [...list.filter((o) => o.path !== option.path), option])
      onChange(option.path)
    },
  })

  if (site.isPending) return <div className="skeleton h-40" />
  if (!site.data?.available || typeof site.data.dem.value !== 'string') return null

  const options: DemOption[] = [
    {
      path: null,
      file: fileName(site.data.dem.value),
      origin: (site.data.dem.source ?? '').split(/[(:]/)[0].replace(/\s+clip.*$/, '').trim(),
      report: site.data.check,
    },
    ...loaded,
  ]
  const selected = options.find((o) => o.path === value) ?? options[0]

  return (
    <section className="panel" data-testid="dem-selector" data-panel="Terrain model">
      <header className="panel-header">
        <h2>Terrain model</h2>
      </header>
      <div className="space-y-3 p-4">
        <div className="flex gap-2">
          <select
            value={selected.path ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
            className="h-10 min-w-0 flex-1 rounded border border-slate-800 bg-slate-950 px-2 font-mono text-14 text-slate-100 focus:border-sky-500 focus:outline-none"
          >
            {options.map((o) => (
              <option key={o.path ?? ''} value={o.path ?? ''}>
                {o.file}
              </option>
            ))}
          </select>
          <label className="flex h-10 shrink-0 cursor-pointer items-center rounded border border-slate-800 bg-slate-950 px-3 text-14 font-medium text-slate-200 transition-colors duration-150 hover:border-sky-700">
            {load.isPending ? 'Checking' : 'Load GeoTIFF'}
            <input
              type="file"
              accept=".tif,.tiff"
              className="hidden"
              disabled={load.isPending}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) load.mutate(file)
                e.target.value = ''
              }}
            />
          </label>
        </div>
        {selected.origin && <div className="text-14 text-slate-400">{selected.origin}</div>}

        {selected.report && (
          <table className="data-table">
            <thead>
              <tr>
                <th>Check</th>
                <th className="num">Value</th>
                <th className="num">Result</th>
              </tr>
            </thead>
            <tbody>
              {checkRows(selected.report).map((row) => (
                <tr key={row.name}>
                  <td className="text-slate-400">{row.name}</td>
                  <td className="num">{row.value}</td>
                  <td className={`num text-12 ${row.ok ? 'text-emerald-400' : 'text-rose-400'}`}>{row.ok ? 'PASS' : 'FAIL'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {load.isError && <p className="text-14 text-rose-400">{(load.error as Error).message}</p>}
      </div>
    </section>
  )
}
